import React, { Fragment } from 'react';
import classNames from 'classnames';
import bowser from 'yola-bowser';
import Icon from '../icon';
import {
  APPEARANCES,
  FONT_SIZES,
  FONT_WEIGHTS,
  FORMATS,
  ICON_PLACEMENTS,
  SIZES,
} from './constants';
import { ActionButtonProps } from './types';
import Loader from '../loader';
import { TYPES as ICON_TYPES } from '../icon/constants';
import noop from '../../../utils/noop';

const ActionButton = ({
  appearance = APPEARANCES.default,
  disabled = false,
  selected = false,
  fontWeight = FONT_WEIGHTS.medium,
  fontSize = FONT_SIZES.medium,
  format = FORMATS.outline,
  fullWidth = false,
  iconGlyph = null,
  iconType = ICON_TYPES.inherit,
  iconPlacement = ICON_PLACEMENTS.before,
  label = null,
  onClick = noop,
  rounded = true,
  size = SIZES.medium,
  isLoading = false,
  href = '',
  target = '_blank',
  ...otherProps
}) => {
  const isMobile = bowser.mobile;
  const isIconBefore = iconPlacement === ICON_PLACEMENTS.before;
  const isIconAfter = iconPlacement === ICON_PLACEMENTS.after;
  const hasLabel = !!label;

  const classes = classNames(
    'ws-ui-action-button',
    'ws-ui-action-button--size',
    `ws-ui-action-button--appearance-${appearance}`,
    `ws-ui-action-button--format-${format}`,
    `ws-ui-action-button--size-${size}`,
    {
      'ws-ui-action-button--full-width': fullWidth,
      'ws-ui-action-button--mobile': isMobile,
      'ws-ui-action-button--rounded': rounded,
      'ws-ui-action-button--icon-before': isIconBefore && iconGlyph && hasLabel,
      'ws-ui-action-button--icon-after': isIconAfter && iconGlyph && hasLabel,
      'ws-ui-action-button--only-icon': !hasLabel,
      [`ws-ui-action-button--font-weight-${fontWeight}`]: fontWeight && hasLabel,
      [`ws-ui-action-button--font-size-${fontSize}`]: fontSize && hasLabel,
      'ws-ui-action-button--selected': selected,
      'ws-ui-action-button--disabled': disabled,
      'ws-ui-action-button--loading': !selected && !disabled && isLoading,
    }
  );

  const iconComponent = iconGlyph ? (
    <span className="ws-ui-action-button__icon">
      <Icon glyph={iconGlyph} type={iconType} size="100%" />
    </span>
  ) : null;

  const loaderComponent = isLoading ? (
    <div className="ws-ui-action-button__loader-wrapper">
      <Loader />
    </div>
  ) : null;

  const buttonContent = (
    <Fragment>
      {isIconBefore && iconComponent}
      {hasLabel && <span className="ws-ui-action-button__label">{label}</span>}
      {isIconAfter && iconComponent}
      {!selected && !disabled && loaderComponent}
    </Fragment>
  );

  if (href) {
    if (disabled) {
      return <span className={classes}>{buttonContent}</span>;
    }

    return (
      <a
        className={classes}
        target={target}
        rel="noreferrer"
        href={href}
        onClick={onClick}
        {...otherProps}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <button
      className={classes}
      disabled={disabled}
      onClick={!isLoading ? onClick : null}
      type="button"
      {...otherProps}
    >
      {buttonContent}
    </button>
  );
};

ActionButton.propTypes = ActionButtonProps;

export default ActionButton;
